import { CSSProperties, FC, useEffect, useState, useMemo, forwardRef } from 'react';
import './Editor.module.scss';
import { Editor as TinyEditor } from '@tinymce/tinymce-react';
import { Editor as EditorType, RawEditorOptions } from 'tinymce';
import appState from '../../../state/AppStateContainer';
import { ErrorMessageIcon } from '../ErrorMessageIcon/ErrorMessageIcon';

//! To upgrade the editor you have to download a new zip file
//! from https://www.tiny.cloud/docs/tinymce/6/react-zip-host/ and
//! put the content of the /js folder from the unzipped folder on /public

//! Note: Language file for the Swedish translation of the editor is at /public/tinymce/langs/sv_SE.js

type Props = {
  value: string,
  onChange: (value: string) => void,
  onEnter?: () => void,
  placeholder?: string,
  isDisabled?: boolean,
  hideToolbar?: boolean,
  hideStatusbar?: boolean,
  wrapperClassName?: string,
  wrapperStyle?: CSSProperties,
  onLoadContent?: (event: any) => void,
  errorMessage?: string,
}

const init : RawEditorOptions & { selector?: undefined; target?: undefined; } = {
  menubar: false,
  branding: false,
  plugins: 'preview importcss searchreplace autolink directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount charmap quickbars emoticons ',
  toolbar: 'bold italic underline forecolor backcolor | fontfamily fontsize blocks alignGroup numlist bullist charmap emoticons preview print image link removeformat | undo redo',
  quickbars_selection_toolbar: 'bold italic underline forecolor backcolor fontsizeinput blocks quicklink blockquote image removeformat',
  quickbars_insert_toolbar: false,
  font_size_formats: '8px 9px 10px 11px 12px 14px 18px 24px 30px 36px 48px 60px 72px 96px',
  resize: false,
  elementpath: false,
  contextmenu: false,
  content_css: ['https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap', '/EditorStyles.css'],
  height: '100%',
  paste_data_images: false,
  block_formats: 'Normal=p;Rubrik 1=h1;Rubrik 2=h2;Rubrik 3=h3;Rubrik 4=h4;',
  toolbar_groups: {
    alignGroup: {
      icon: "align-left",
      items: "alignleft aligncenter alignright alignjustify"
    }
  },
};

type LangCodes = {
  [key: string]: string;
}

const langCodes: LangCodes = {
  sv: 'sv_SE',
  en: 'en',
  nb: 'nb_NO',
  da: 'da',
}

const Editor: FC<Props> = ({ value, onChange, onEnter, placeholder, isDisabled, hideToolbar, hideStatusbar, wrapperClassName, wrapperStyle, onLoadContent, errorMessage }) => {
  const tinyInit = useMemo(() => ({
    ...init,
    placeholder: placeholder,
    language: langCodes[appState.state.language] || 'sv_SE',
    statusbar: !hideStatusbar,
    toolbar: hideToolbar ? false : init.toolbar,
  }), [placeholder, appState.state.language]);

  return (
    <div className={`${wrapperClassName || ''} editorWrapper`} style={{ position: 'relative', height: '100%', width: '100%', ...wrapperStyle }}>
      <TinyEditor
        key={appState.state.language}
        tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
        init={tinyInit}
        value={value}
        onEditorChange={newValue => {
          onChange(newValue);
        }}
        onKeyUp={(e) => {
          if (onEnter && e.key === 'Enter' && !e.shiftKey) {
            onEnter();
          }
        }}
        onLoadContent={onLoadContent}
        disabled={isDisabled}
      />
      {errorMessage && <div style={{ position: 'absolute', bottom: '50%', right: '16px', transform: 'translateY(50%)' }}><ErrorMessageIcon errorMessage={errorMessage} /></div>}
    </div>
  );
}

export default Editor;
